<script lang="ts" setup>
import type { CheckCallWithUpdate } from "@/api/trips/dto/check-call";
import TripTimer from "@/views/trips/components/TripTimer.vue";
import type { TravelOrderWithTruck, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { computed } from "vue";
import { RouteStatus } from "@/constants/trip";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";
import BrokerUpdatesTable from "@/views/trips/view/components/BrokerUpdatesTable.vue";

const { t } = useI18n();

const props = defineProps<{
  travelOrder: TravelOrderWithTruck;
  checkCalls: CheckCallWithUpdate[];
  route: TripRouteDto | undefined;
}>();

const emits = defineEmits<{
  (e: "viewUpdate", checkCall: CheckCallWithUpdate): void;
  (e: "updateMonitor"): void;
  (e: "openCreateUpdate", travelOrder: TravelOrderWithTruck, checkCall: CheckCallWithUpdate): void;
  (e: "openIntervalDialog", travelOrder: TravelOrderWithTruck): void;
}>();

const customer = computed(() => {
  return props.travelOrder.order.customer;
});

const showTimer = computed(() => {
  return customer.value.sendUpdates;
});

const relatedUpdates = computed(() => {
  return props.checkCalls
    .filter((checkCall) => {
      const isRouteIdMatched = props.travelOrder.routeId === checkCall.routeId;

      if (!isRouteIdMatched) {
        return;
      }

      const travelOrderIds = checkCall.travelOrderIds;

      if (!travelOrderIds?.length) {
        return true;
      }

      return props.travelOrder.id && travelOrderIds.includes(props.travelOrder.id);
    })
    .reverse();
});

const isPlanned = computed(() => {
  return props.route?.status === RouteStatus.planned;
});

function resolveTravelOrder() {
  return props.travelOrder;
}

function isCreateDisabled(item: CheckCallWithUpdate) {
  return Boolean(item.deletedBy) || isPlanned.value || !customer.value.sendUpdates;
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <div class="font-size-16 font-weight-500 text-gray-darken-3 mr-4">Travel order #{{ travelOrder.id }}</div>

        <router-link
          :to="{ name: 'truck-view', params: { id: travelOrder.truck.id } }"
          class="text-decoration-none"
          target="_blank"
        >
          <v-icon class="mr-1" color="primary" size="14">mdi-truck-outline</v-icon>
          <span class="font-size-12 font-weight-500 text-primary">Truck: #{{ travelOrder.truck.number }}</span>
        </router-link>
      </div>

      <div v-if="showTimer" class="d-flex align-center">
        <span class="text-grey-darken-1 mr-3">New update in:</span>
        <permission-tooltip :permission="DispatcherPermissionValue.CreateBrokerUpdate">
          <template #activator="{ disabled }">
            <trip-timer
              :disabled="isPlanned || disabled"
              :interval="travelOrder.updateInterval"
              :next-update-time="travelOrder.nextUpdateTime"
              :truck-id="travelOrder.truck.id"
              :truck-number="travelOrder.truck.number"
              @open-dialog="emits('openIntervalDialog', travelOrder)"
            ></trip-timer>
          </template>

          {{ t("permission.create-broker-update") }}
        </permission-tooltip>
      </div>
    </div>

    <broker-updates-table
      :check-calls="relatedUpdates"
      :is-create-disabled="isCreateDisabled"
      :resolve-travel-order="resolveTravelOrder"
      @view-update="emits('viewUpdate', $event)"
      @open-create-update="(to, cc) => emits('openCreateUpdate', to, cc)"
    />
  </div>
</template>

<style lang="scss" scoped></style>
